/**
 * QC vocabulary — the Quality (QC operator) module of the seedtrac manual, chapter 4.1.
 *
 * This is the QA gate's own language, and it is deliberately NOT `quality.ts`. That file holds
 * `prelimQuality`: what the unloading operator eyeballs at arrival, stored on the document line.
 * What lives here is the lab pipeline that decides whether a lot may move:
 *
 *     QC Sampling Request ──▶ QC Sample Pending ──▶ QC Result Update
 *     (mints a sample per lot)  (collect + print slip)  (germination / PP / moisture / verdict)
 *
 * A sample carries the four tests the request ticked (`P/M/G/T` on every seedtrac grid) and walks
 * one status machine. `lots.qc_status` is a CACHE of where its live sample got to — `qcStatusForLot`
 * below is the only mapping, so the two can never be given different opinions by two call sites.
 *
 * Scope note: chapter 4.1 has nine transactions; this covers the three that make up the sample
 * lifecycle. Guard samples, GOT dispatch and the standalone PP & Moisture patch are not modelled
 * here yet, and `docs/PLAN.md` §5.2 sketches a much larger model (standards, ISTA sample sizes,
 * test packets, dispositions) for when QCS/QCM land. The manual is the authority for what ships now.
 */
import { z } from "zod";

/**
 * What a `qc_samples` row can BE. The `qc_sample_status` pgEnum is this list, in this order.
 *
 * `under_testing` and `retest` are both "awaiting a result" and both appear on the manual's QC
 * Result Pending List — they are separate states because the list shows them apart (`UT` vs `RT`)
 * and because a retest is the second attempt, which is the thing a QC manager escalates on.
 */
export const QC_SAMPLE_STATUSES = [
  "requested",
  "under_testing",
  "retest",
  "passed",
  "failed",
  // Wasted, or sent for new sampling (QC fail rules, Phase 2): a replacement drawn from the same
  // lot carries on in its place. The old packet and its readings stay as history.
  "replaced",
  // Invalid germination (user, 2026-10-24): a germination not carried out properly is run again from
  // planting with the same setup, on this sample or a fresh one. QC-only: the lot reads UT to
  // everyone else (`qcStatusForLot`), and an API returning a raw sample status outside QC masks it.
  "invalid",
] as const;
export type QcSampleStatus = (typeof QC_SAMPLE_STATUSES)[number];
export const qcSampleStatus = z.enum(QC_SAMPLE_STATUSES);

/** Full labels, for a detail screen with room for them. */
export const QC_SAMPLE_STATUS_LABELS: Record<QcSampleStatus, string> = {
  requested: "Sample Pending",
  under_testing: "Under Testing",
  retest: "Retest",
  passed: "Passed",
  failed: "Failed",
  replaced: "Replaced",
  invalid: "Invalid",
};

/** The two-letter codes the seedtrac grids print in their QC Status column. */
export const QC_SAMPLE_STATUS_CODES: Record<QcSampleStatus, string> = {
  requested: "SP",
  under_testing: "UT",
  retest: "RT",
  passed: "OK",
  failed: "F",
  replaced: "RP",
  invalid: "INV",
};

/**
 * With the lab, awaiting a result: the statuses the QC Result Pending List is made of. An Invalid
 * germination is being run again, so it waits for a result like UT and RT.
 */
export const QC_LAB_STATUSES = ["under_testing", "retest", "invalid"] as const;

/** `QC_LAB_STATUSES` as an SQL list, for `qs.status in (...)`. */
export const QC_LAB_STATUSES_SQL = QC_LAB_STATUSES.map((x) => `'${x}'`).join(
  ", "
);

export function awaitsResult(status: QcSampleStatus): boolean {
  return (QC_LAB_STATUSES as readonly string[]).includes(status);
}

/**
 * The sample status a reader outside QC may see: Invalid is QC's own, so everyone else reads it as
 * Under Testing (user, 2026-10-24). `qcUser` is a login holding `qc.view`.
 */
export function sampleStatusFor<T extends string | null>(
  status: T,
  qcUser: boolean
): T | "under_testing" {
  return status === "invalid" && !qcUser ? "under_testing" : status;
}

/**
 * A test's tray by name, never a round number (user, 2026-10-31: "round 1 round 2 is confusing"):
 * the API's own name ("Original", "Repeat after Invalid", "Fresh sample after Invalid", "Retest N",
 * from `attemptNameSql`), else what the round number alone can tell, for an older API.
 */
export function attemptLabel(
  attempt: string | null | undefined,
  round = 1
): string {
  if (attempt) return attempt;
  return round <= 1 ? "Original" : `Retest ${round - 1}`;
}

/** A QC Result Update attempt by name: the first is the Original, each later one a Retest. */
export const resultAttemptName = (attemptNo: number): string =>
  attemptNo <= 1 ? "Original" : `Retest ${attemptNo - 1}`;

/** Nothing further happens to the sample. */
export function isTerminal(status: QcSampleStatus): boolean {
  return status === "passed" || status === "failed" || status === "replaced";
}

/**
 * The Result dropdown on QC Result Updation. The manual never shows it expanded; these three are
 * the tick boxes printed on the Seed Testing Slip it generates ("Result (Tick) OK | Retest | Fail").
 */
export const QC_RESULTS = ["ok", "retest", "fail"] as const;
export type QcResult = (typeof QC_RESULTS)[number];
export const qcResult = z.enum(QC_RESULTS);

export const QC_RESULT_LABELS: Record<QcResult, string> = {
  ok: "OK",
  retest: "Retest",
  fail: "Fail",
};

/** Where a recorded result leaves the sample. */
export function statusAfterResult(result: QcResult): QcSampleStatus {
  return result === "ok" ? "passed" : result === "fail" ? "failed" : "retest";
}

/**
 * What `lots.qc_status` can BE. The `qc_status` pgEnum is this list, in this order.
 *
 * Wider than `QC_SAMPLE_STATUSES` because a lot also carries states no sample produces: `expired`
 * from the nightly sweep, `quarantined` and `released` from a disposition.
 */
export const LOT_QC_STATUSES = [
  "not_applicable",
  "under_testing",
  "eligible",
  "expired",
  "pending_review",
  "released",
  "rejected",
  "quarantined",
] as const;
export type LotQcStatus = (typeof LOT_QC_STATUSES)[number];

/**
 * The two-letter codes the seedtrac grids print in their QC Status column, for a LOT.
 *
 * `UT` and `OK` are deliberately the codes `QC_SAMPLE_STATUS_CODES` gives the sample statuses they
 * project from (see `qcStatusForLot` below), so a lot and its live sample can never read
 * differently. A lot with no sample is `not_applicable`, which the grids print as a dash.
 */
export const LOT_QC_STATUS_CODES: Record<LotQcStatus, string> = {
  not_applicable: "—",
  under_testing: "UT",
  eligible: "OK",
  expired: "EX",
  pending_review: "PR",
  released: "RL",
  rejected: "RJ",
  quarantined: "QN",
};

/**
 * The code for a `lots.qc_status` value that reached us as a bare string (a wire row, a jsonb read).
 * An unrecognised value is printed as-is rather than blanked, so a future enum member stays visible.
 */
export function lotQcStatusCode(status: string): string {
  return LOT_QC_STATUS_CODES[status as LotQcStatus] ?? status;
}

/**
 * The sample status projected onto `lots.qc_status`, whose enum is wider because it also carries
 * states no sample produces (`expired` from the nightly sweep, `quarantined` and `released` from a
 * disposition). A lot with no live sample is `not_applicable`, which is why this returns only the
 * four a sample can justify.
 */
export function qcStatusForLot(
  status: QcSampleStatus
): "under_testing" | "eligible" | "rejected" {
  if (status === "passed") return "eligible";
  if (status === "failed") return "rejected";
  return "under_testing";
}

/**
 * Which tests the request asked for. At least one must be ticked — the manual marks the whole
 * checkbox group `*`, not the individual boxes.
 */
export const qcTests = z.object({
  pp: z.boolean(),
  moisture: z.boolean(),
  germination: z.boolean(),
  got: z.boolean(),
});
export type QcTests = z.infer<typeof qcTests>;

/** Every QC sample includes Germination: it is the only test that can fail a lot (user, 2026-10-20). */
export const qcTestsRequested = qcTests.refine((t) => t.germination, {
  message: "Germination is required on every QC sample",
  path: ["germination"],
});

export const QC_TEST_LABELS: Record<keyof QcTests, string> = {
  pp: "PP",
  moisture: "Moisture",
  germination: "Germination",
  got: "GOT",
};

/**
 * The `QC Tests` cell every QC grid prints: the initials of the ticked tests, slash separated, in
 * the lab's order — Moisture, Physical Purity, Germination, then GOT spelled out (QC team,
 * 2026-10-16: "M -> P -> G -> T", T renamed GOT): `M/P/G`, `M/P/G/GOT`, `G`. Empty when nothing is
 * ticked, which the grids render as a blank cell rather than a dash.
 */
export function qcTestCode(tests: QcTests): string {
  const parts: string[] = [];
  if (tests.moisture) parts.push("M");
  if (tests.pp) parts.push("P");
  if (tests.germination) parts.push("G");
  if (tests.got) parts.push("GOT");
  return parts.join("/");
}

/** Whether a result value is required, given what the request asked for. */
export function requiresGermination(tests: QcTests): boolean {
  return tests.germination;
}

/**
 * How a request selects its lots. `single_lot` posts one lot at a time into a line list;
 * `multi_lot` takes a crop/variety and sweeps every eligible lot under it (manual p126–127).
 */
export const QC_REQUEST_SCOPES = ["single_lot", "multi_lot"] as const;
export type QcRequestScope = (typeof QC_REQUEST_SCOPES)[number];
export const qcRequestScope = z.enum(QC_REQUEST_SCOPES);

export const QC_REQUEST_SCOPE_LABELS: Record<QcRequestScope, string> = {
  single_lot: "Single Lot",
  multi_lot: "Multiple Lots",
};

/**
 * Who raised a request. `arrival` is an arrival's submit raising samples for the lots it minted,
 * which seedtrac's pending list shows as an `AR` Opr Code; `manual` is anyone raising one by hand.
 * The `qc_request_source` pgEnum is this list, in this order.
 */
export const QC_REQUEST_SOURCES = ["manual", "arrival"] as const;
export type QcRequestSource = (typeof QC_REQUEST_SOURCES)[number];

/** The pending list's Opr Code cell. nexseed has no per-login operator codes, only where it came from. */
export const QC_REQUEST_SOURCE_CODES: Record<QcRequestSource, string> = {
  manual: "QC",
  arrival: "AR",
};

export function qcRequestSourceCode(source: string): string {
  return QC_REQUEST_SOURCE_CODES[source as QcRequestSource] ?? source;
}

/**
 * The request `reason` a blend's own sample carries. A blend raises its request by hand-raise rules
 * (`source` manual), so the reason is what tells QC Sample Pending it came from Blending.
 */
export const QC_REASON_BLENDING = "Blending";

/**
 * Why a sample is waiting, as QC Sample Pending's Reason column says it (QC team, 2026-10-16):
 * Arrival (a vehicle arrival raised it), Blending, Priming (a primed lot's retest), or Pending
 * (raised by hand; a supervisor's Resample / Rework reason rides along as `note`). `softReleased`
 * adds the Soft Release tag: the lot already moved on a Soft Release while its tests are still to
 * run.
 */
export type QcSampleReason = "Arrival" | "Blending" | "Priming" | "Pending";
export function qcSampleReason(r: { source: string; reason: string | null }): {
  reason: QcSampleReason;
  note: string | null;
} {
  if (r.source === "arrival") return { reason: "Arrival", note: null };
  if (r.reason === QC_REASON_BLENDING)
    return { reason: "Blending", note: null };
  // A primed lot's retest (docs/designs/priming.md); shared/priming.ts QC_REASON_PRIMING.
  if (r.reason === "Priming") return { reason: "Priming", note: null };
  return { reason: "Pending", note: r.reason?.trim() || null };
}

/**
 * A sample's SLOC cell as bins only. `slocSummary` is one `path | bags | qty` line per bin; QC
 * Sample Pending shows NoB and Qty in their own columns (QC team, 2026-10-16: Qty showed twice), so
 * the SLOC column keeps just the paths.
 */
export function qcSlocBins(summary: string | null | undefined): string[] {
  if (!summary) return [];
  return summary
    .split("\n")
    .map((l) => l.split("|")[0]!.trim())
    .filter(Boolean);
}

/**
 * A sample's `slocSummary` split into its bins, for tables that show SLOC as Bin | NoB | Qty
 * sub-columns. A line without the two figures keeps its path with empty bags and qty.
 */
export function qcSlocLines(
  summary: string | null | undefined
): { path: string; bags: string; qty: string }[] {
  if (!summary) return [];
  return summary
    .split("\n")
    .map((l) => {
      const [path = "", bags = "", qty = ""] = l
        .split("|")
        .map((p) => p.trim());
      return { path, bags, qty };
    })
    .filter((l) => l.path);
}

/**
 * The three stages QC reads a lot in (QC team, 2026-10-16): Raw (Cob counts as Raw), Conditioned,
 * and Conditioned + Treated, a conditioned or packed lot whose processing slip recorded a
 * treatment. Null when the lot has no stage.
 */
export const QC_LOT_STAGE_GROUPS = [
  "Raw",
  "Conditioned",
  "Conditioned + Treated",
] as const;
export type QcLotStageGroup = (typeof QC_LOT_STAGE_GROUPS)[number];
export function qcLotStageGroup(
  stageCode: string | null | undefined,
  treated: boolean
): QcLotStageGroup | null {
  const code = (stageCode ?? "").trim().toUpperCase();
  if (code === "R" || code === "CB") return "Raw";
  if (code === "C" || code === "P")
    return treated ? "Conditioned + Treated" : "Conditioned";
  return null;
}

/**
 * What a QC Sample Slip prints, once, for both printers: the desk's PDF sheet and the handheld's
 * built-in label printer. `spCode` is the `F X M` pair the handheld slip prints in its Variety row
 * (seedtrac's Sample Generation app); the desk sheet prints the variety name.
 */
export interface QcSlip {
  sampleNo: string;
  crop: string | null;
  variety: string | null;
  spCode: string | null;
  lotNumber: string;
  stage: string | null;
  nob: number | null;
  qty: string | null;
  /**
   * Which packet this copy goes on (`Working Sample – Moisture`, `Guard Sample`, ...); null on the handheld's one
   * slip for the pooled sample bag, and on a copy past the named ones.
   */
  copyLabel?: string | null;
  /** Who drew the sample on the floor. */
  sampledBy?: string | null;
  /**
   * Printed before every bag of the lot was drawn: the NoB/Qty row is left off (user, 2026-10-22),
   * since more bags may still be weighed.
   */
  early?: boolean;
}

/**
 * The packets a sample is split into at the lab, in print order, each named in full on its slip:
 * one Working Sample per test the lab runs on it (each goes to its own bench), the Guard Sample kept
 * back, and the Grow-Out Test packet. seedtrac's samplers pen-marked these W.S / G.S / GOT (deck
 * `Sampling.pptx`); the desk's slip sheet now prints the names out (user, 2026-09-24).
 */
export const QC_SLIP_COPY_LABELS = [
  "Working Sample – Moisture",
  "Working Sample – Physical Purity",
  "Working Sample – Germination",
  "Guard Sample",
  "GOT Sample",
] as const;

/** The tests a sample was raised for, which decide its packets. */
export interface QcSlipTests {
  testMoisture: boolean;
  testPp: boolean;
  testGermination: boolean;
  testGot: boolean;
}

/**
 * One label per copy. With no count, one copy per packet the sample needs: a Working Sample for each
 * of moisture, physical purity and germination it was raised for, the Guard Sample always, and the
 * GOT Sample only when a Grow-Out Test was asked for. An explicit count labels the first copies in
 * that order and leaves any extra ones unlabelled.
 */
export function qcSlipCopyLabels(
  tests: QcSlipTests,
  copies?: number
): (string | null)[] {
  const [moisture, pp, germination, guard, got] = QC_SLIP_COPY_LABELS;
  const named: string[] = [
    ...(tests.testMoisture ? [moisture] : []),
    ...(tests.testPp ? [pp] : []),
    ...(tests.testGermination ? [germination] : []),
    guard,
    ...(tests.testGot ? [got] : []),
  ];
  const n = copies ?? named.length;
  return Array.from({ length: n }, (_, i) => named[i] ?? null);
}

/**
 * Where an arrival-raised sample stands on the floor, before it is collected. The sample exists from
 * the moment its lot is added to the arrival; the sampler draws from every bag as the truck unloads,
 * and the slip may print and the sample go to the lab only once unloading is complete and every
 * weighed bag has been drawn (seedtrac's Sample Generation prints at `Bags N/N`).
 */
export const QC_ARRIVAL_STAGES = ["unloading", "sampling", "ready"] as const;
export type QcArrivalStage = (typeof QC_ARRIVAL_STAGES)[number];

export interface QcSampleReadiness {
  /** Bags drawn: scanned tags, or the sampler's count on a lot weighed without tags. */
  bagsDrawn: number;
  /** Bags weighed on the arrival line so far. */
  bagsTotal: number;
  hasBagTags: boolean;
  unloadComplete: boolean;
}

/** True when the slip may print and the sample may be collected. */
export function qcSampleReady(r: QcSampleReadiness): boolean {
  return r.unloadComplete && r.bagsTotal > 0 && r.bagsDrawn >= r.bagsTotal;
}

/**
 * The floor slip may print from the first bag drawn: sampling and printing run side by side (user,
 * 2026-10-22: "print the slip and continue the sampling work"). Until the lot is ready the slip
 * leaves NoB/Qty off, since more bags may still be weighed.
 */
export function qcSlipPrintable(r: QcSampleReadiness): boolean {
  return r.bagsDrawn > 0 || qcSampleReady(r);
}

export function qcArrivalStage(r: QcSampleReadiness): QcArrivalStage {
  if (qcSampleReady(r)) return "ready";
  return r.bagsDrawn > 0 ? "sampling" : "unloading";
}

/**
 * The chip on the pending list and the lot card: where unloading stands AND where sampling stands,
 * with the bag count, so a sampler reads both at a glance (user, 2026-09-26):
 *   `Unloading · Sampling not started (0/12)`, `Unloading · Sampled 5/12`,
 *   `Unloaded · Sampling not started (0/40)`, `Unloaded · Sampled 30/40`, `Ready to collect (40/40)`.
 * The count is bags drawn / bags weighed so far -- while the truck unloads the second grows.
 */
export function qcArrivalStageLabel(r: QcSampleReadiness): string {
  const count = `${r.bagsDrawn}/${r.bagsTotal}`;
  if (qcSampleReady(r)) return `Ready to collect (${count})`;
  const unload = r.unloadComplete ? "Unloaded" : "Unloading";
  if (r.bagsTotal === 0) return `${unload} · No bags weighed yet`;
  return r.bagsDrawn > 0
    ? `${unload} · Sampled ${count}`
    : `${unload} · Sampling not started (${count})`;
}

/** Unloading is over and nobody has started drawing: the one state a supervisor should chase. */
export const qcSamplingOverdue = (r: QcSampleReadiness): boolean =>
  r.unloadComplete && r.bagsTotal > 0 && r.bagsDrawn === 0;

/** Why the slip cannot print yet, in the words the floor uses; null when it can. */
export function qcNotReadyReason(r: QcSampleReadiness): string | null {
  if (!r.unloadComplete) return "Unloading is not marked complete yet.";
  if (r.bagsTotal === 0) return "No bags have been weighed on this lot yet.";
  if (r.bagsDrawn < r.bagsTotal)
    return `Only ${r.bagsDrawn} of ${r.bagsTotal} bags have been sampled.`;
  return null;
}

/**
 * Lab work waits for the arrival's Submit (user, 2026-09-30: "until a txn is submitted QC can't be
 * performed, only sampling"). Before it the floor may draw bags and print the slip; collection onto
 * an SRF, the tests, their verification and the result come after. An arrival sample gets its lot
 * at Submit, so one on an arrival line with no lot is still waiting.
 */
export const qcAwaitingArrivalSubmit = (x: {
  documentLineId: number | null;
  lotId: number | null;
}): boolean => x.documentLineId !== null && x.lotId === null;

export const QC_AWAITING_SUBMIT_REASON =
  "The arrival is not submitted yet. Only sampling is allowed until then.";

/**
 * Why a requested sample cannot be collected yet, the arrival's Submit first; null when it can.
 * `ready` is the server's readiness (a hand-raised sample is always ready).
 */
export function qcCollectBlockedReason(
  r: QcSampleReadiness & {
    ready: boolean;
    documentLineId: number | null;
    lotId: number | null;
  }
): string | null {
  if (qcAwaitingArrivalSubmit(r)) return QC_AWAITING_SUBMIT_REASON;
  return r.ready ? null : qcNotReadyReason(r);
}

/**
 * Where a truck stands on the handheld's Sample Generation list, and on the desk's progress band:
 * still being set up, unloading, drawn from until every lot is ready, then waiting for its floor
 * slips. It leaves the list once every lot is ready and printed.
 */
export const QC_TRUCK_STAGES = [
  "setup",
  "unloading",
  "sampling",
  "print",
] as const;
export type QcTruckStage = (typeof QC_TRUCK_STAGES)[number];

export const QC_TRUCK_STAGE_LABELS: Record<QcTruckStage, string> = {
  setup: "Setup",
  unloading: "Unloading",
  sampling: "Sampling",
  print: "Print slips",
};

export function qcTruckStage(t: {
  setupStage: string;
  unloadStage: string;
  lotsTotal: number;
  lotsReady: number;
}): QcTruckStage {
  if (t.setupStage !== "complete") return "setup";
  if (t.unloadStage !== "complete") return "unloading";
  if (t.lotsTotal === 0 || t.lotsReady < t.lotsTotal) return "sampling";
  return "print";
}

/**
 * The two chips on a truck card (user, 2026-10-01): Unloading and Sampling side by side, each
 * `live` (same colour, blinking dot) while it is going on and `done` (green) once over. `warn` is
 * sampling not started after unloading finished -- `qcSamplingOverdue` at truck level.
 */
export type QcTruckChipState = "idle" | "live" | "done" | "warn";
export interface QcTruckChip {
  label: string;
  state: QcTruckChipState;
}
export interface QcTruckChips {
  unloading: QcTruckChip;
  sampling: QcTruckChip;
  /**
   * Bags still to draw on the truck, beside "Sampling x/y" on the handheld (user, 2026-10-14: show
   * how many are pending or left). 0 once everything is drawn, or when nothing has been weighed.
   */
  left: number;
}

export function qcTruckChips(t: {
  unloadStage: string;
  lotsTotal: number;
  lotsReady: number;
  bagsDrawn: number;
  bagsTotal: number;
}): QcTruckChips {
  const unloaded = t.unloadStage === "complete";
  const unloading: QcTruckChip = unloaded
    ? { label: "Unloaded", state: "done" }
    : t.unloadStage === "in_progress"
      ? { label: "Unloading", state: "live" }
      : { label: "Not unloading", state: "idle" };
  let sampling: QcTruckChip;
  if (t.lotsTotal > 0 && t.lotsReady >= t.lotsTotal) {
    sampling = { label: "Sampled", state: "done" };
  } else if (t.bagsDrawn > 0) {
    sampling = {
      label: `Sampling ${t.bagsDrawn}/${t.bagsTotal}`,
      state: "live",
    };
  } else if (unloaded && t.bagsTotal > 0) {
    sampling = { label: "Sampling not started", state: "warn" };
  } else {
    sampling = { label: "Not sampled", state: "idle" };
  }
  const left =
    sampling.state === "done" ? 0 : Math.max(0, t.bagsTotal - t.bagsDrawn);
  return { unloading, sampling, left };
}

/** A truck is live on the floor while either of its chips is. */
export const qcTruckLive = (c: QcTruckChips): boolean =>
  c.unloading.state === "live" || c.sampling.state === "live";

/**
 * The QA Dashboard's limits (user, 2026-10-20; docs/designs/qa-dashboard.md). One place, so the
 * API's red counts and the web's red rows cannot disagree.
 */
export const QA_LIMITS = {
  /** Unloading complete and sampling not started. */
  samplingStartHours: 2,
  /** Sampling started and the truck not yet Sampled. */
  samplingDoneHours: 12,
  /** Moisture / PP from reaching the lab (or a retest round opening) to the reading's submission. */
  benchHours: 24,
  /** A submitted reading waiting for the Supervisor's verification. */
  verifyHours: 24,
  /** Under Testing lots older than this count on the Manager's strip. */
  utDays: 7,
  /** QC validity ending inside this many days. */
  validityDays: 7,
} as const;

/** One stage per truck on the Supervisor's truck table, in floor order. */
export const QA_TRUCK_STAGES = [
  "arriving",
  "unloading",
  "not_started",
  "sampling",
  "sampled",
] as const;
export type QaTruckStage = (typeof QA_TRUCK_STAGES)[number];

/**
 * A truck's single stage: sampling outranks unloading, so a truck still unloading with bags already
 * drawn is "sampling" (sampling runs during unloading), never counted twice.
 */
export function qaTruckStage(t: {
  unloadStage: string;
  lotsTotal: number;
  lotsReady: number;
  bagsDrawn: number;
  bagsTotal: number;
}): QaTruckStage {
  if (qcTruckChips(t).sampling.state === "done") return "sampled";
  if (t.bagsDrawn > 0) return "sampling";
  if (t.unloadStage === "complete") return "not_started";
  if (t.unloadStage === "in_progress") return "unloading";
  return "arriving";
}

/** `F X M`, or null when neither parent code is known. */
export function qcSpCode(
  spFemale: string | null | undefined,
  spMale: string | null | undefined
): string | null {
  if (!spFemale && !spMale) return null;
  return `${spFemale ?? ""} X ${spMale ?? ""}`.trim();
}

/** Percentages on the result form. Two decimals is what the lab records; the grids print one. */
export const qcPercent = z.number().min(0).max(100);

/**
 * Life Expectancy (LE) Duration in months. 24 is the seedtrac default on the result form; the
 * ceiling matches the `document_lines.le_months` check constraint so the two cannot disagree.
 */
export const QC_LE_MONTHS_DEFAULT = 24;
export const qcLeMonths = z.number().int().min(1).max(240);

/** Number series prefixes. The counters live in the database; see `buildDocumentNumber`. */
export const QC_REQUEST_SERIES = "QCS";
/** The old sample series (`QCSMP/000001/Y/D`), kept so numbers minted before G7 still resolve. */
export const QC_SAMPLE_SERIES = "QCSMP";

/**
 * A sample number the way legacy QC-App reads it: plant letter, year-code letter and a six-digit
 * running number, `DY162697` (8 characters, the length the legacy GOT / BTS / Germination screens
 * insist on). The counter never restarts, so a number is never reused at a plant. Samples minted
 * before 2026-10-07 carry no year letter (`D162697`) and stay valid; so does a date no year code
 * covers.
 */
export function buildQcSampleNo(
  plantCode: string,
  yearCode: string,
  n: number
): string {
  return `${plantCode.toUpperCase()}${yearCode.toUpperCase()}${String(n).padStart(6, "0")}`;
}

/** The sequence the short numbers are counted in: per plant, never reset. */
export const QC_SAMPLE_NO_SEQUENCE = "qc:sample-no";

/**
 * `SRF-D26-00001`: seedtrac's own SRF number (plant code, two-digit year, five-digit running number),
 * kept because the lab's printed forms already read that way. The counter restarts each year.
 */
/**
 * Samples on one SRF (QC team, 2026-10-16): the 21st sample collected opens the next SRF, so a
 * form never runs past the 20 rows the lab's sheet holds.
 */
export const SRF_SAMPLE_LIMIT = 20;

export function buildSrfNumber(
  plantCode: string,
  year: number,
  seq: number
): string {
  const yy = String(year % 100).padStart(2, "0");
  return `SRF-${plantCode}${yy}-${String(seq).padStart(5, "0")}`;
}

/** SRF for RT Lots (seedtrac QCS p177–178): `SRF-RT-D26-00001`, its own running number per plant and year. */
export function buildRtSrfNumber(
  plantCode: string,
  year: number,
  seq: number
): string {
  return buildSrfNumber(plantCode, year, seq).replace(/^SRF-/, "SRF-RT-");
}
